/**
 * What this page tells the platform it does with audio: iOS 17+ Safari's
 * Audio Session API. The property is absent everywhere else, where every
 * call here is a no-op.
 *
 * `playback` whenever no microphone is open. Safari starts a page as
 * `ambient`, the category the hardware mute switch silences, and a tutor
 * nobody can hear because of a switch on the side of the phone looks
 * exactly like a tutor that does not work. `playback` is not silenced, and
 * it routes an iPhone to the loudspeaker.
 *
 * `play-and-record` only while a microphone stream is live. A capture stream
 * puts the page in a record category whatever it declares, and that category
 * routes an iPhone's output to the earpiece. Declaring it for longer than the
 * stream lives is what kept every later "Hear it" quiet (media-channel design
 * §1). Counted, because a stream that closes must not hand `playback` to one
 * still open (§4.1).
 */
type SessionNavigator = Navigator & { audioSession?: { type: string } };

export class AudioSessionClaims {
  private recording = 0;
  private readonly listeners = new Set<() => void>();
  // Assigned in the body: node's strip-only loader rejects parameter properties.
  private readonly nav: Navigator;

  constructor(nav: Navigator) {
    this.nav = nav;
  }

  // Arrows, for `useSyncExternalStore`, which calls them unbound. The update
  // notice waits while a microphone is open, and this is the one count of
  // them.
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  isRecording = (): boolean => this.recording > 0;

  playback(): void {
    if (this.recording === 0) this.claim('playback');
  }

  recordingStarted(): void {
    this.recording++;
    this.claim('play-and-record');
    this.notify();
  }

  recordingStopped(): void {
    this.recording = Math.max(0, this.recording - 1);
    if (this.recording === 0) this.claim('playback');
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private claim(type: 'playback' | 'play-and-record'): void {
    // Narrowed rather than cast to `any`, so a future DOM lib that types the
    // property conflicts here loudly instead of being shadowed.
    const session = (this.nav as SessionNavigator).audioSession;
    if (!session) return;
    try {
      session.type = type;
    } catch {
      // A browser that exposes the property but rejects the value is telling
      // us it disagrees about what this page may claim. The audio still
      // plays, in whatever category it picked.
    }
  }
}

export const audioSession = new AudioSessionClaims(globalThis.navigator);
